import { motion } from "framer-motion";
import { useT } from "@/i18n/context";

function Shimmer({ className = "" }: { className?: string }) {
  return (
    <div className={`relative overflow-hidden rounded-md bg-white/[0.06] ${className}`}>
      <motion.div
        initial={{ x: "-100%" }}
        animate={{ x: "100%" }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
        className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent"
      />
    </div>
  );
}

export default function LandingSkeleton() {
  const t = useT();
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className="landing-root fixed inset-0 z-[200] overflow-hidden bg-grayScale-900 text-white"
    >
      {/* Ambient orbs */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute -right-40 top-10 h-[600px] w-[600px] rounded-full bg-primary-main/20 blur-[140px]"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute -left-40 bottom-0 h-[600px] w-[600px] rounded-full bg-secondary-main/15 blur-[140px]"
          animate={{ scale: [1.15, 1, 1.15] }}
          transition={{ duration: 7, repeat: Infinity }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
      </div>

      {/* Header skeleton */}
      <header className="relative z-10 mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-6">
        <div className="flex items-center gap-3">
          <Shimmer className="h-10 w-10 rounded-xl" />
          <div className="hidden flex-col gap-1.5 sm:flex">
            <Shimmer className="h-3 w-28" />
            <Shimmer className="h-2 w-20" />
          </div>
        </div>
        <nav className="hidden items-center gap-2 lg:flex">
          {Array.from({ length: 6 }).map((_, i) => (
            <Shimmer key={i} className="h-3.5 w-16" />
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Shimmer className="h-9 w-9 rounded-full" />
          <Shimmer className="hidden h-3.5 w-10 md:block" />
          <Shimmer className="h-9 w-28 rounded-lg" />
        </div>
      </header>

      {/* Hero skeleton */}
      <main className="relative z-10 mx-auto grid max-w-7xl gap-14 px-6 pt-20 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:pt-28">
        {/* Copy column */}
        <div className="space-y-7">
          <Shimmer className="h-7 w-72 rounded-full" />
          <div className="space-y-4">
            <Shimmer className="h-12 w-[90%]" />
            <Shimmer className="h-12 w-[75%]" />
            <Shimmer className="h-12 w-[60%]" />
          </div>
          <div className="space-y-2">
            <Shimmer className="h-3.5 w-[85%]" />
            <Shimmer className="h-3.5 w-[78%]" />
            <Shimmer className="h-3.5 w-[55%]" />
          </div>
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <Shimmer className="h-12 w-52 rounded-full" />
            <div className="flex items-center gap-3">
              <Shimmer className="h-12 w-12 rounded-full" />
              <Shimmer className="h-3.5 w-32" />
            </div>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3 pt-2">
            <Shimmer className="h-3 w-28" />
            <Shimmer className="h-3 w-32" />
            <Shimmer className="h-3 w-36" />
          </div>
        </div>

        {/* Showcase column */}
        <div className="relative mx-auto h-[420px] w-full max-w-[520px] sm:h-[520px]">
          <div className="absolute inset-0 rounded-[2.5rem] border border-white/10 bg-white/[0.04] backdrop-blur-sm" />
          <div className="absolute right-8 left-8 top-8 bottom-24 overflow-hidden rounded-[2rem] border border-white/10 bg-grayScale-900/70 p-5">
            {/* mock topbar */}
            <div className="mb-4 flex items-center justify-between border-b border-white/5 pb-3">
              <div className="flex gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-white/15" />
                <div className="h-2.5 w-2.5 rounded-full bg-white/15" />
                <div className="h-2.5 w-2.5 rounded-full bg-white/15" />
              </div>
              <Shimmer className="h-2.5 w-20" />
              <Shimmer className="h-2.5 w-10" />
            </div>
            {/* mock rows */}
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Shimmer className="h-9 w-9 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Shimmer className="h-3 w-[60%]" />
                    <Shimmer className="h-2.5 w-[85%]" />
                  </div>
                  <Shimmer className="h-2.5 w-10" />
                </div>
              ))}
            </div>
          </div>
          {/* orbit dots */}
          {Array.from({ length: 5 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute bottom-6 h-10 w-10 rounded-2xl border border-white/10 bg-white/[0.06]"
              style={{ left: `${10 + i * 18}%` }}
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.15 }}
            />
          ))}
        </div>
      </main>

      {/* Center loader pill */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 rounded-full border border-white/10 bg-white/[0.06] px-5 py-2.5 backdrop-blur-xl"
      >
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.12 }}
              className="h-1.5 w-1.5 rounded-full bg-primary-main"
            />
          ))}
        </div>
        <span className="text-[11px] font-bold tracking-wide text-grayScale-300">
          {t("landing.skeleton.loading", "در حال بارگذاری ترندی اسمارت…")}
        </span>
      </motion.div>

      <span className="sr-only">{t("landing.skeleton.loadingSr", "در حال بارگذاری صفحه…")}</span>
    </div>
  );
}
